/* eslint-disable no-underscore-dangle */
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import flatten from 'lodash/flatten';
import { useQuery } from '@apollo/client';

import { errorService } from 'global/services';

import {
    SECRET_FILTER_ID,
    STANDARD_FILTER_ID,
    IN_15_MINS,
    VEGAN,
    NY_DISHES,
    NY_DESSERT,
} from 'app/utils/filters';
import USER_QUERY from 'app/graphql/network/auth/userQuery.graphql';
import {
    parseMenuType,
} from 'app/utils/basketSchema';

import MENU_DISHES_QUERY from 'app/graphql/network/menuDishes';

import { allDishesListVar } from 'app/apollo/reaction';
import { ConfigContext } from './ConfigProvider';
import { FiltersContext } from './FiltersProvider';


const getEnabledDishes = (options) => {
    const {
        period,
        periodsConfig,
        dishCategories,
    } = options;

    const flattenCategoriesDishes = flatten(
        Object.values(dishCategories).map((c) => {
            const { enabledDishes, enabledAdditionalDishes } = c;
            const ed1 = enabledDishes || [];
            const ed2 = enabledAdditionalDishes || [];
            return ed1.concat(ed2);
        }),
    );

    if (periodsConfig?.length) {
        const periodData = periodsConfig.find((pc) => pc.period === period);
        if (!periodData) return flattenCategoriesDishes;
        return flatten(Object.values(periodData.dishes));
    }

    return flattenCategoriesDishes;
};


const filterDishes = (options) => {
    const {
        allDishes: allDishesArg,
        filterTag,
        filterCategories,
        filterByIds,
        exclidingFilterTag,
        excludingCategories,
        filterParentDishes,
        pageId,
    } = options;

    if (!allDishesArg.length) {
        return [];
    }

    const allDishes = allDishesArg
        .filter((d) => {
            if (!exclidingFilterTag) return d;
            return !d.tags.find((t) => t.id === exclidingFilterTag);
        })
        .filter((d) => {
            if (!filterParentDishes) return d;
            return String(d.parentId) === d.id;
        });

    const filterTagArray = filterTag ? [filterTag] : [];
    const filterTags = Array.isArray(filterTag) ? filterTag : filterTagArray;

    if (filterTags.length > 0) {
        const disabledCategories = excludingCategories || [];

        const filteredDishes = allDishes.filter((dish) => {
            if (!dish.tags) return false;
            const filteredTag = dish.tags.find((t) => filterTags.includes(t.id));
            const isCategoryEnabled = !disabledCategories.includes(String(dish.categoryId));
            return (Boolean(filteredTag) && isCategoryEnabled);
        });

        if (filterByIds) {
            const filteredDishesByIds = allDishes.filter((dish) => {
                const { id } = dish;
                return filterByIds.includes(id);
            });

            return [
                ...filteredDishes,
                ...filteredDishesByIds,
            ];
        }

        return filteredDishes;
    }

    if (filterCategories && filterCategories.length) {
        const filteredDishes = allDishes.filter((dish) => {
            const cat = String(dish.categoryId);
            return filterCategories.includes(cat);
        });
        return filteredDishes;
    }

    if (filterByIds) {
        const filteredDishes = allDishes.filter((dish) => {
            const { id } = dish;
            return filterByIds.includes(id);
        });

        /*
            TODO: сделать нормальную возможность иметь один диш в двух категориях
        */
        if (filterByIds.includes('3433') && pageId !== 'christmas' && pageId !== 'old_ny') {
            const dd = allDishes.find((d) => d.id === '3433');
            if (dd) {
                filteredDishes.push(dd);
                filteredDishes.push(dd);
            }
        }
        return filteredDishes;
    }

    const enabledDishes = getEnabledDishes(options);

    return enabledDishes
        .map((dishId) => {
            const dishObj = allDishes.find((e) => e.id === dishId);
            return dishObj;
        })
        .filter((dish) => Boolean(dish));
};


export const MenuDishesContext = createContext({
    dishes: [],
});


/*
    перечисление тегов, чтобы получить все нужные блюда
*/
const DEFAULT_QUERY_TAGS = [
    SECRET_FILTER_ID,
    STANDARD_FILTER_ID,
    IN_15_MINS,
    VEGAN,
    NY_DISHES,
    NY_DESSERT,
];


const getQueryTagsWithUserData = (queryTags, userTags) => {
    if (queryTags) {
        return [...new Set([...queryTags, ...userTags])];
    }
    return [...new Set([...DEFAULT_QUERY_TAGS, ...userTags])];
};

export function MenuDishesProvider(props) {
    const {
        children,
    } = props;

    const { pageId, isCustomizationEnabled } = useContext(ConfigContext);

    const { period } = useContext(FiltersContext);
    const userData = useQuery(USER_QUERY, {
        context: {
            massage: 'app:init:MenuDishesProvider',
        },
    });

    const {
        dishCategories,
        periods: periodsConfig,
        filterTag,
        filterCategories,
        menuType: configMenuType,
        queryTags,
        filterByIds,
        exclidingFilterTag,
        excludingCategories,
        filterParentDishes,
    } = useContext(ConfigContext);

    // const userExclidingFilterTags = userData.data?.user?.customizationTags || [];
    const userExclidingFilterTags = userData.data?.user?.filterTags || [];

    const tags = getQueryTagsWithUserData(queryTags, userExclidingFilterTags);

    const menuType = parseMenuType(window.location) || configMenuType;

    useEffect(() => {
        if (!period) {
            errorService.log({
                source: 'client',
                text: 'Menu dishes period is empty',
                scope: 'PromoCheckoutMenuDishesProvider.js',
                error: 'Menu dishes period is empty',
                dishesQueryVariables: {
                    period,
                    tags,
                    menuType,
                },
            });
        }
    }, [period, tags, menuType]);

    const { data } = useQuery(
        MENU_DISHES_QUERY,
        {
            variables: { period, tags, type: menuType },
            onCompleted: ({ menuDishes }) => allDishesListVar(menuDishes),
        },
    );

    const contextValue = useMemo(() => {
        const allDishes = data ? data.menuDishes : [];

        const mockYandexMarketDish = {
            id: 'ym', tags: [], portions: [1], price: 0,
        };

        const filteredDishes = filterDishes({
            period,
            allDishes: [
                ...allDishes,
                mockYandexMarketDish,
            ],
            dishCategories,
            periodsConfig,
            filterTag,
            filterCategories,
            filterByIds,
            exclidingFilterTag,
            excludingCategories,
            filterParentDishes,
            pageId,
        });

        const checkedForParentExistingDishes = isCustomizationEnabled
            ? filteredDishes
            : filteredDishes
                .map((dish) => {
                    const isParentDish = dish.id === String(dish.parentId);
                    const hasItsParentNearby = isParentDish
                        ? false
                        : filteredDishes.some((e) => e.id === String(dish.parentId));
                    return {
                        ...dish,
                        hasItsParentNearby,
                    };
                })
                .filter((e) => !e.hasItsParentNearby);

        const value = { dishes: checkedForParentExistingDishes };
        return value;
    }, [
        data, dishCategories, exclidingFilterTag, excludingCategories, filterByIds,
        filterCategories, filterParentDishes, filterTag, period, periodsConfig, isCustomizationEnabled,
    ]);

    return (
        <MenuDishesContext.Provider value={contextValue}>
            {children}
        </MenuDishesContext.Provider>
    );
}

MenuDishesProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};
